import EventBus from '../services/service.eventBus';
import { fetchVehicleImage } from '../services/service.vehicle';
import { state as offerVehicleState } from '../store/store.offer-vehicle';
import { ANSWER_LEAD_TYPE_OPT_IN } from '../utils/utils.answers';
import { QUESTION_LEAD_TYPE, pluckQuestion } from '../utils/utils.question';
import { capitalizedFirstLeter } from '../utils/utils.vehicle';
import { vehicleBodyType } from '../utils/utils.vehicle-view';

const onVehicleImageFetch = (trimID: number): void => {
  EventBus.publish('VehicleImageFetch', trimID);

  fetchVehicleImage(trimID)
    .then((res) => {
      onVehicleImageFetchSuccess(res);
    })
    .catch((error) => {
      onVehicleImageFetchFail(error);
    });
};

const onVehicleImageFetchSuccess = (imageUrl): void => {
  offerVehicleState.imageUrl = imageUrl;

  EventBus.publish('VehicleImageFetchSuccess', imageUrl);
};

const onVehicleImageFetchFail = (error): void => {
  EventBus.publish('VehicleImageFetchFail', error);
  console.log(error);
};

const onOfferLoad = (offer) => {
  const { prospect } = offer;
  const { genericOptions, history, mileage, options, replacementVehicle, vehicle, dealershipsAndInventory } = prospect;
  const { vehicleDescription } = vehicle;

  onVehicleDetailsSet(vehicle);
  onVehicleDescriptionSet(vehicleDescription);
  onVehicleMileageSet(mileage);
  onVehicleHistorySet(history);
  onVehicleReplacementSet(replacementVehicle, dealershipsAndInventory);
  onOfferOptionsSet(genericOptions, options);

  EventBus.publish('OfferLoadVehicle', { history, mileage, vehicle });
};

const onOfferLoadWithVehicleImage = (offer) => {
  const { prospect } = offer;
  const { vehicle } = prospect;
  const { trimId } = vehicle;

  onVehicleImageFetch(trimId);
  onOfferLoad(offer);
};

const onOfferLoadWithoutVehicleImage = (offer) => {
  onOfferLoad(offer);
};

const onInProgressVehicleLoad = ({ vehicleDescription, vehicleOptions, vehicleHistory, replacementVehicle }) => {
  offerVehicleState.inProgressVehicle.description = vehicleDescription;
  offerVehicleState.inProgressVehicle.options = vehicleOptions;
  offerVehicleState.inProgressVehicle.history = vehicleHistory;
  offerVehicleState.inProgressVehicle.replacementVehicle = replacementVehicle;
};

// This sets the options that are consumed when the offer is rendered (as opposed to the options that are submitted to the Offer API)
const onOfferOptionsSet = (genericOptions = [], options = []) => {
  let optionList = [];
  if (genericOptions.length > 0) {
    const installedOptions = genericOptions.filter((option) => option.isInstalledEquipment).map((o) => o.genericName);
    optionList = Array.from(new Set(installedOptions));
  } else {
    const userSelectedOptions = options.filter((option) => option.isSelected).map((option) => option.displayName);
    optionList = Array.from(new Set(userSelectedOptions));
  }

  offerVehicleState.offerOptions = optionList;
  EventBus.publish('onOfferOptionsSet', optionList);
};

const onVehicleDetailsSet = (vehicleDetails) => {
  const { colorId, drivetrainId, engineId, makeId, modelId, transmissionId, trimId, vin, yearId, drivetrain } = vehicleDetails;

  // explicitly listing out props so they are searchable
  // by other devs when debugging...
  offerVehicleState.colorId = colorId;
  offerVehicleState.drivetrainId = drivetrainId;
  offerVehicleState.engineId = engineId;
  offerVehicleState.makeId = makeId;
  offerVehicleState.modelId = modelId;
  offerVehicleState.transmissionId = transmissionId;
  offerVehicleState.trimId = trimId;
  offerVehicleState.vin = vin;
  offerVehicleState.yearId = yearId;
  offerVehicleState.drivetrain = drivetrain;

  EventBus.publish('VehicleDetailsSet', { colorId, drivetrainId, engineId, makeId, modelId, transmissionId, trimId, vin, yearId });
};

const onVehicleDescriptionSet = (vehicleDescription) => {
  const { color, drivetrain, engine, make, model, transmission, trim, year, bodyStyle } = vehicleDescription;
  offerVehicleState.color = color;
  offerVehicleState.drivetrain = drivetrain;
  offerVehicleState.engine = engine;
  offerVehicleState.fullName = `${year} ${make} ${model} ${trim}`;
  offerVehicleState.yearMakeModel = `${year} ${make} ${model}`;
  offerVehicleState.make = make;
  offerVehicleState.model = model;
  offerVehicleState.transmission = transmission;
  offerVehicleState.trim = trim;
  offerVehicleState.vehicleType = vehicleBodyType(trim?.toLowerCase(), model?.toLowerCase());
  offerVehicleState.year = year;
  offerVehicleState.bodyStyle = bodyStyle.toUpperCase() === 'SUV' ? bodyStyle.toUpperCase() : capitalizedFirstLeter(bodyStyle);

  EventBus.publish('VehicleDescriptionSet', { color, drivetrain, engine, make, model, transmission, trim, year });
};

function subQuestionAnswers(subquestions) {
  return subquestions
    .filter((q) => q.value)
    .map((q) => q.label)
    .toString();
}

const cleanReplacementVehicleTypes = (text) => {
  return text.replaceAll(' cars', '').replaceAll(',', ', ');
};

const onVehicleReplacementSet = (replacementVehicle, dealershipsAndInventory) => {
  const replacementQuestion = pluckQuestion(replacementVehicle, QUESTION_LEAD_TYPE);
  const allocationEntries = dealershipsAndInventory?.allocationEntries || [];
  const { voiMake, voiModel } = allocationEntries[0]?.dealerInfo || {};
  const lookingFor = subQuestionAnswers(pluckQuestion(replacementVehicle, 'qrv/LookingFor').subquestions);
  const makeModel = subQuestionAnswers(pluckQuestion(replacementVehicle, 'qrv/MakeAndModel').subquestions);
  const range = pluckQuestion(replacementVehicle, 'qrv/YearRange');
  const replacementMakeId = pluckQuestion(replacementVehicle, 'qrv/MakeAndModel').subquestions[0].hasOwnProperty('value')
    ? pluckQuestion(replacementVehicle, 'qrv/MakeAndModel').subquestions[0]?.value.split('/')[1]
    : '';
  const replacementModelId = pluckQuestion(replacementVehicle, 'qrv/MakeAndModel').subquestions[0].hasOwnProperty('value')
    ? pluckQuestion(replacementVehicle, 'qrv/MakeAndModel').subquestions[0]?.value.split('/')[2]
    : '';

  offerVehicleState.hasReplacement = replacementQuestion.value === ANSWER_LEAD_TYPE_OPT_IN;
  offerVehicleState.replacementTypes = cleanReplacementVehicleTypes(lookingFor);
  offerVehicleState.replacementName = makeModel;
  offerVehicleState.replacementMakeId = replacementMakeId;
  offerVehicleState.replacementModelId = replacementModelId;
  offerVehicleState.replacementMake = voiMake;
  offerVehicleState.replacementModel = voiModel;
  offerVehicleState.replacementVehicleYearMin = range.min;
  offerVehicleState.replacementVehicleYearMax = range.max;
};

const onVehicleHistorySet = (history) => {
  const hasAccidents = pluckQuestion(history, 'qp/124').value;
  const isAuctionedLast45 = pluckQuestion(history, 'qp/81').value;
  const isRental = pluckQuestion(history, 'qp/125').value;
  const hasSingleOwner = pluckQuestion(history, 'qp/83').value;
  const hasTwoSetsOfKeys = pluckQuestion(history, 'qp/79').value;

  offerVehicleState.hasAccidents = hasAccidents;
  offerVehicleState.hasSingleOwner = hasSingleOwner;
  offerVehicleState.isAuctionedLast45 = isAuctionedLast45;
  offerVehicleState.isRental = isRental;
  offerVehicleState.hasTwoSetsOfKeys = hasTwoSetsOfKeys;

  offerVehicleState.history = history;

  EventBus.publish('VehicleHistorySet', { hasAccidents, hasSingleOwner, isAuctionedLast45, isRental, hasTwoSetsOfKeys });
};

const onVehicleMileageSet = (mileage) => {
  EventBus.publish('VehicleMileageSet', mileage);

  offerVehicleState.mileage = mileage;
};

const actions = {
  onOfferLoad,
  onOfferLoadWithVehicleImage,
  onOfferLoadWithoutVehicleImage,
  onVehicleImageFetch,
  onVehicleImageFetchSuccess,
  onVehicleImageFetchFail,
  onVehicleDetailsSet,
  onInProgressVehicleLoad,
};

export { actions };
export default actions;
